import { z } from 'zod';
import { INSTRUCTIONS_MAX, INSTRUCTIONS_REFUSAL } from '../../src/domain/kit/instructions';
import {
  CalendarDateSchema,
  CarrierSchema,
  CaseReleaseSchema,
  DeletionStatusSchema,
  IdSchema,
  InputTypeSchema,
  InstantSchema,
  JobOrderNoSchema,
  OmittedIdentityIdSchema,
  OmittedIdentitySchema,
  OmittedMoneySchema,
  OrderStateSchema,
  OriginSchema,
  RowVersionSchema,
  ShippingMethodSchema,
  ShippingStatusSchema,
  StageSchema,
} from './common.schema';
import {
  CasePhotoSchema,
  ItemsReceivedSchema,
  KitSchema,
  KitSpecificationSchema,
  StageHistoryEntrySchema,
  TeethSchema,
  ToothNumberSchema,
  UndoRefSchema,
} from './kit.schema';
import { NewSpanSchema, SpanSchema } from './span.schema';
import { DeliverToSchema } from './geo.schema';
import { AccountsReviewedSchema } from './accountsReview.schema';
import { PaymentStatusSchema } from './receipt.schema';
import {
  REDO_LEGACY_NUMBER_MAX,
  REDO_REASON_MAX,
  REDO_REFUSAL,
} from '../../src/domain/order/redo';
import { DELETION_REASON_MAX, DELETION_REFUSAL } from '../../src/domain/order/deletion';
import {
  DRIVER_NAME_MAX,
  SHIPPING_REFUSAL,
  WAYBILL_CODE_MIN,
} from '../../src/domain/order/shipping';
import { CARRIER_OTHER_MAX, CARRIER_OTHER_MIN } from '../../src/domain/catalog/catalog';
import { ORIGIN_REFUSAL } from '../../src/domain/order/origin';
import { SERIAL_NUMBER_MAX, SERIAL_NUMBER_REFUSAL } from '../../src/domain/order/serialNumber';

export { NewSpanSchema, SPAN_KINDS, SpanSchema } from './span.schema';
export { DELETION_STATUSES } from '../../src/domain/order/deletion';
export { SHIPPING_METHODS, SHIPPING_STATUSES } from '../../src/domain/order/shipping';

/**
 * A deletion on a job order, shaped like `hold` and `dispatch` beside it.
 *
 * `none` carries nothing. `pending` is one person's request, waiting for somebody
 * else holding approveDeletion; the case is not locked by it and works on as
 * before. `deleted` has left every list, and is purged on `purgesAt`.
 */
export const DeletionSchema = z.object({
  status: DeletionStatusSchema,
  reason: z.string().nullable(),
  requestedByUserId: IdSchema.nullable(),
  requestedByUserName: z.string().nullable(),
  requestedAt: InstantSchema.nullable(),
  decidedByUserId: IdSchema.nullable(),
  decidedByUserName: z.string().nullable(),
  decidedAt: InstantSchema.nullable(),
  /** When the case actually left the floor. Null while a request is pending. */
  deletedAt: InstantSchema.nullable(),
  /** deletedAt plus 30 days, as a lab calendar date. Null until deleted. */
  purgesAt: CalendarDateSchema.nullable(),
});

/**
 * The archive on a job order, shaped like `deletion` beside it. Section 4 of the
 * request of 24 September 2026. A case that is not archived is `archived` false
 * with every other field null. An archived case is on no list of work in hand,
 * and in the archive's Archived tab until it is taken out again.
 */
export const JobOrderArchiveSchema = z.object({
  archived: z.boolean(),
  archivedAt: InstantSchema.nullable(),
  archivedByUserId: IdSchema.nullable(),
  archivedByUserName: z.string().nullable(),
  /** Why, when somebody said. */
  reason: z.string().nullable(),
});

/**
 * The case a remake names. A job order of this system, `JO-2054`, or, when the
 * order's `redoOfLegacy` says so, the number somebody typed for a case from
 * before it, which follows no pattern: one to thirty two characters. The
 * request of 6 October 2026 (second), item 1.
 */
export const RedoOfSchema = z.string().min(1).max(REDO_LEGACY_NUMBER_MAX);

export const JobOrderSchema = z.object({
  jobOrderNo: JobOrderNoSchema,

  // Section 7.10 item 1. Absent for a caller without seeIdentity.
  dentistName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  /**
   * The doctor's and the patient's records the case is linked to. Sent only
   * where the two names are; null on a case that was never linked. The request
   * of 6 October 2026 (second), items 4 and 5.
   */
  doctorId: OmittedIdentityIdSchema,
  patientId: OmittedIdentityIdSchema,
  /**
   * Where the case is delivered, with the four keys of identity above and
   * absent with them: only for a caller with seeIdentity. Null when the case
   * has no doctor's record. The request of 6 October 2026 (fifth), section 2.
   */
  deliverTo: DeliverToSchema.nullable().optional(),

  /**
   * Where the case comes from: local, international or online. Section 1 of the
   * request of 24 September 2026, which put these three in place of the
   * country. (The API still sends `country` beside it for a device on the build
   * before; nothing here reads it.)
   */
  origin: OriginSchema,
  kitCount: z.number().int().positive(),
  /**
   * After `kitCount`, in this order, on every job order wherever it is served.
   * The request of 6 October 2026 (fifth), section 4. `createdAt` is the
   * instant the case was booked; `input` is the first tray's, `digital` or
   * `impression`, null on a case with no tray; `product` is the first tray's
   * item, null on a case with no tray and for a tray whose item is blank. The
   * first tray is the one with the lowest id. The API always sends the three;
   * they are optional here only so a copy of the app that meets an API older
   * than they are still reads a job order.
   */
  createdAt: InstantSchema.optional(),
  input: InputTypeSchema.nullable().optional(),
  product: z.string().nullable().optional(),
  state: OrderStateSchema,
  urgent: z.boolean(),
  deliveryDate: CalendarDateSchema,
  daysLeft: z.number().int(),
  /**
   * What a list of cases can show in a column. The lab's request of
   * 11 October 2026: more columns to switch on, on Orders as on Final Check and
   * the production line. The day the case was received is `createdAt`. After
   * `daysLeft`, in this order: `kitCount` to `state` stand as they were.
   * `startDate` is the day work starts; `units` the units of every tray;
   * `shade` and `teeth` the first tray's, null and empty on a case with no
   * tray; `noteCount` the notes everybody on the case reads and `photoCount`
   * its case photographs, counted as a tray counts its own; `minutesInStage`
   * how long the first tray has been at its department, null once the case has
   * left the lab. The API always sends them; optional here only so a copy of
   * the app that meets an API older than they are still reads a job order.
   */
  startDate: CalendarDateSchema.optional(),
  units: z.number().int().nonnegative().optional(),
  shade: z.string().nullable().optional(),
  teeth: z.array(z.string()).optional(),
  noteCount: z.number().int().nonnegative().optional(),
  photoCount: z.number().int().nonnegative().optional(),
  minutesInStage: z.number().int().nonnegative().nullable().optional(),

  // Section 7.10 item 2. Absent without pay or admin.
  value: OmittedMoneySchema,

  /**
   * The case was edited after intake and Accounts has not yet squared the bill with it:
   * when, and what changed (no doctor or patient names). Null when nothing waits; absent
   * without pay or admin. Request of 5 October 2026.
   */
  accountsReview: z
    .object({ since: InstantSchema, changes: z.string() })
    .nullable()
    .optional(),

  /**
   * The review that stands on the case: when Accounts marked its changes reviewed and who
   * did. Null when none stands, because none was made or it was taken back; absent without
   * pay or admin. A case edited again after its review carries both this and
   * `accountsReview`. The request of 5 October 2026 (third), section 3.
   */
  accountsReviewed: AccountsReviewedSchema.nullable().optional(),

  payment: z.object({
    paid: z.boolean(),
    paidAt: InstantSchema.nullable(),
    /*
     * The request of 8 October 2026 (fourth): a case may be paid in parts. `status`
     * is for everybody, as `paid` always was; the three figures are money, absent
     * without pay or admin as the case's value is. All four are absent from an API
     * older than they are.
     */
    status: PaymentStatusSchema.optional(),
    due: OmittedMoneySchema,
    paidAmount: OmittedMoneySchema,
    balance: OmittedMoneySchema,
  }),
  hold: z.object({
    held: z.boolean(),
    reason: z.string().nullable(),
    heldAt: InstantSchema.nullable(),
  }),
  /**
   * How the case left and where it stands on its way to the customer. The name
   * stays `dispatch`, because every screen, the archive and the case record read
   * it. Request of 13 September 2026, section 1.5.
   */
  dispatch: z.object({
    /** True from the moment it leaves the lab, whichever route. Drives OrderState. */
    dispatched: z.boolean(),
    status: ShippingStatusSchema,
    method: ShippingMethodSchema.nullable(),

    /** The courier. Null on a lab delivery. */
    carrier: z.string().nullable(),
    /** Typed by hand from the waybill, never generated. Null until it arrives. */
    trackingCode: z.string().nullable(),
    /** Who drove it, on a lab delivery. Free text, optional. */
    driverName: z.string().nullable(),
    note: z.string().nullable(),

    /** When it left the lab. The billing date, and what the archive sorts by. */
    dispatchedAt: InstantSchema.nullable(),
    /** When the code was entered, so the lab can see how long the courier took. */
    trackedAt: InstantSchema.nullable(),
    /** When the customer confirmed they had it. Null until somebody ticks it. */
    deliveredAt: InstantSchema.nullable(),
    /**
     * Who signed for it, or null. Absent for a caller without seeIdentity, like
     * the two names above: the person who signs at a clinic may be the doctor or
     * the patient, and a technician may read this order.
     */
    receivedBy: OmittedIdentitySchema,
    /**
     * When somebody in the lab marked the case received, and who. The moment the
     * mark was made, which `deliveredAt` need not be: the day the customer had it
     * may be entered afterwards. Stamped where `deliveredAt` goes from null to a
     * value, cleared where it goes back to null, and left alone when the day is
     * only moved. Null until then, and on a case received before the two were
     * kept; absent from an API older than they are. Lab staff, not identity, so
     * every caller receives them. The request of 5 October 2026 (third),
     * section 11.
     */
    receivedMarkedAt: InstantSchema.nullable().optional(),
    receivedMarkedByUserId: IdSchema.nullable().optional(),
    /** The name that person carries today. */
    receivedMarkedByUserName: z.string().nullable().optional(),
  }),

  /**
   * Where the case is right now, and who has it.
   *
   * This replaced a mini pipeline of seven segments on the orders row. The
   * client wanted the department and the technician's name instead, which is
   * what somebody chasing a case actually needs: not how far along it is, but
   * whose bench to walk to.
   *
   * `currentStage` is null once the order is dispatched. `holderName` is null
   * when nobody has been assigned yet, which is a real and common state.
   */
  currentStage: StageSchema.nullable(),
  holderName: z.string().nullable(),
  /** The bridges and titanium bars on the case. See `span.schema.ts`. */
  spans: z.array(SpanSchema),

  /** The case this one is a remake of, or null. See `RedoOfSchema`. */
  redoOf: RedoOfSchema.nullable(),
  /**
   * The original is from before this system: `redoOf` is its old job order
   * number as typed, and names no case here, so it is shown as text and never
   * as a link. False on every other case, a remake of a case of this system
   * included.
   */
  redoOfLegacy: z.boolean(),
  /** The reason this case came back, recorded when the redo was raised. */
  redoReason: z.string().nullable(),
  /**
   * The job order that remakes this one, once it has been sent back, or null.
   *
   * The other end of `redoOf`. The case record reads the order, not an archive
   * row, and it has to say "Sent back as JO-2091" and link there.
   */
  redoneAs: JobOrderNoSchema.nullable(),

  deletion: DeletionSchema,
  archive: JobOrderArchiveSchema,

  /**
   * The number on the paper job order handed to the doctor, or null. The lab's
   * request of 17 October 2026, item 7. Neither money nor identity, so every
   * caller is sent it, and `q` finds a case by it. Absent from an API older
   * than it.
   */
  serialNumber: z.string().nullable().optional(),
  /**
   * Released by Accounts to be shipped before it is paid, or null. It clears
   * the case for dispatch as a payment does, so a finished released case has
   * the state `paid`, Pending dispatch, while `payment.paid` stays false: it is
   * still owed. Item 8 of the same request. Absent from an API older than it.
   */
  release: CaseReleaseSchema.nullable().optional(),

  rowVersion: RowVersionSchema,
});

/** `GET /job-orders/{jo}` returns the order with its kits. */
export const JobOrderWithKitsSchema = z.object({
  jobOrder: JobOrderSchema,
  kits: z.array(KitSchema),
});

/**
 * Intake, section 7.5.
 *
 * `deliveryDate` is optional and the server recalculates it unless the caller
 * holds `editDates`. The server is authoritative for both the delivery date and
 * `daysLeft`; the intake form recomputes only for the live preview while the
 * user is choosing a start date.
 */
export const CreateJobOrderSchema = z.object({
  /*
   * The doctor and the patient, each by the id of its record or by name. The
   * request of 6 October 2026 (second), items 4 and 5.
   *
   * `doctorId` names a doctor's record and must exist, or the answer is 400 on
   * `doctorId`, "Choose a doctor from the list." Without it `dentistName` is
   * required, and is resolved by the server: the oldest doctor of that name,
   * compared trimmed, with inner white space collapsed and without regard to
   * case, or a new doctor with only the name. A name sent beside an id is not
   * read. The case keeps the resolved records' names.
   *
   * The patient is resolved apart from the doctor, since the request of
   * 6 October 2026 (third), item 3: a patient is one record whichever doctor
   * they are seen by. `patientId` names a patient's record and must exist, or
   * the answer is 400 on `patientId`, "Choose a patient from the list."
   * Without it `patientName` is required, and is the oldest patient of that
   * name, under whichever doctor, or a new one when nobody has it. With
   * `newPatient: true` beside the name a new record is always made, for a
   * different person of the same name; it is read only beside a name that was
   * typed, and never beside `patientId`.
   *
   * An id is a string of digits; absent, null and an empty string all mean it
   * was not sent. A name that is missing where one is required is a 400 on
   * `dentistName` or `patientName`, "Enter the doctor's name." or "Enter the
   * patient's name.", and one longer than 160 characters, "Keep it to 160
   * characters". The two names are the first thing the server checks, before
   * anything else about the case, and it answers with the first of them only.
   */
  dentistName: z.string().nullish(),
  patientName: z.string().nullish(),
  doctorId: z.string().nullish(),
  patientId: z.string().nullish(),
  newPatient: z.boolean().nullish(),
  /**
   * The job order serial number: the number on the paper job order handed to
   * the doctor. Optional, trimmed, up to 32 characters; longer is a 400 on this
   * field, "Keep the job order serial number to 32 characters." On an edit,
   * left off keeps it, and null or an empty string takes it off. The lab's
   * request of 17 October 2026, item 7.
   */
  serialNumber: z.string().trim().max(SERIAL_NUMBER_MAX, SERIAL_NUMBER_REFUSAL.tooLong).nullish(),
  /*
   * In place of the country, section 1 of the request of 24 September 2026.
   *
   * The server also still accepts `country` in its place, reading the UAE as
   * local and any other country as international, so a handset still running
   * yesterday's build can take a case in. That is not a shape to newly send.
   */
  origin: z.enum(OriginSchema.options, { errorMap: () => ({ message: ORIGIN_REFUSAL.choose }) }),
  input: InputTypeSchema,
  product: z.string().min(1, 'Choose a product'),
  /*
   * The teeth may be empty. Reception often takes a case over the phone
   * knowing the count but not yet the FDI numbers, so a case can be opened on
   * `units` alone and the chart filled in later.
   *
   * Each tooth carries its own item. Omitting `product` means "the same as the
   * case", which is what most cases are and what every case created before this
   * field existed was; the server fills it in from `product` above.
   *
   * The server also still accepts a bare array of strings, which is what every
   * client sent before this field existed. That is deliberately not offered
   * here: it is there so a handset still running yesterday's build can create a
   * case -- the service worker updates on prompt, not on its own -- and not a
   * shape anything should newly send.
   */
  teeth: z.array(
    z.object({
      tooth: ToothNumberSchema,
      product: z.string().min(1).optional(),
    }),
  ),
  /**
   * How many teeth the case covers. Never below the number selected on the
   * chart: the two are the same fact, and a count that disagreed with the
   * chart would put a tray on the bench that says one thing and a sheet that
   * says another.
   */
  units: z.number().int().positive('Enter how many teeth this case covers'),
  /**
   * The bridges and titanium bars. Validated against `teeth` with the rules in
   * `src/domain/teeth/spans.ts`; a failure is a 400 with `errors.spans`
   * carrying one message per offending span, in the order sent.
   */
  spans: z.array(NewSpanSchema).default([]),
  shade: z.string().min(1),
  /*
   * The specification and what arrived with the case, both set at intake.
   *
   * They were on the kit and on the printed sheet from the start, and until
   * now the server invented them. The client asked for reception to fill them
   * in, which is where they come from on paper.
   */
  specification: KitSpecificationSchema,
  itemsReceived: ItemsReceivedSchema,
  startDate: CalendarDateSchema,
  deliveryDate: CalendarDateSchema.optional(),
  /**
   * The instructions on the case: one field, "Instructions" on screen. Section
   * 5 of the request of 5 October 2026, which took the lab's own
   * `labInstructions` out of the contract. Up to 5,100 characters; a longer
   * text is a 400 on this field.
   */
  instructions: z.string().max(INSTRUCTIONS_MAX, INSTRUCTIONS_REFUSAL.tooLong),

  /*
   * A remake, booked in at intake. Section 7 of the request of 16 September
   * 2026, which supersedes the archive's own redo endpoint.
   *
   * The case arrives back at the lab in a box, somebody at reception opens it
   * and books it in, and that is the same motion as any other case. NOTHING is
   * copied from the original: a remake is rarely identical, the dentist has
   * usually sent a new impression or a different shade, and a prefilled form
   * invites somebody to accept last time's values without reading them.
   */
  /**
   * The case this one remakes. Requires the `redo` permission. A job order in
   * the archive, or, with `redoOfLegacy`, the old number of a case from before
   * this system: one to thirty two characters once trimmed, checked by the
   * server after the permission, in its own words. Left blank, the case is no
   * remake.
   */
  redoOf: z.string().nullish(),
  /**
   * The original is from before this system, so there is nothing to find in
   * the archive: `redoOf` is its old job order number as typed, kept trimmed.
   * The request of 6 October 2026 (second), item 1. Checked in this order: 403
   * without `redo`, as every remake; 400 on `redoOf` when the number is
   * missing ("Enter the job order number of the original case"), longer than
   * thirty two characters ("Keep the job order number to 32 characters"), or
   * exactly the number of a job order of this system, a deleted one included
   * ("That case is in this system. Find it in the archive instead."); 400 on
   * `redoReason`, which is still required; and 409 when that old number was
   * already sent back, counted among the remakes of old cases only. Not
   * chargeable and priced at zero, as every remake is.
   */
  redoOfLegacy: z.boolean().optional(),
  /** Why it came back. Required when `redoOf` is set. */
  redoReason: z.string().max(REDO_REASON_MAX, REDO_REFUSAL.reasonTooLong).nullish(),
  /**
   * Marked urgent while the case is being created, rather than only afterwards.
   * Requires `markUrgent` and is IGNORED without it rather than refused: the
   * field is a convenience, and a 403 on a checkbox nobody can see would be a
   * puzzle.
   */
  urgent: z.boolean().optional(),

  /**
   * The bench the case opens at. Section 4 of the request of 20 September 2026.
   *
   * Defaults to the first stage AFTER Intake on the case's own route: CAD / CAM
   * for a digital case, Plaster / Model for an impression one. Every case used to
   * open at `intake`, which is a bench only for somebody carrying the intake
   * operator flag, so a case booked in by reception sat where reception could not
   * work it and the bench that should have had it could not see it.
   *
   * Intake stays a stage, a bench and part of every route, for a case that
   * genuinely needs receiving work first; the selector can still choose it.
   * `ready` and `finalcheck` are refused: a case cannot begin where it is
   * supposed to end.
   */
  startStage: StageSchema.optional(),
});

/**
 * `PATCH /job-orders/{jo}`: editing a case after intake. System test handover 2.4.
 *
 * Every field of the create command, each optional, except the two dates,
 * which have their own endpoint. Send only what changes. `teeth` keeps the
 * create shape and replaces the chart; `spans`, when present, replaces the
 * whole list of bridges and bars and is validated as it is on create, against
 * the chart the case will have.
 *
 * The doctor and the patient are resolved as on create, by `doctorId` or
 * `dentistName` and by `patientId` or `patientName`, with `newPatient` beside
 * a typed name for a second record of it. What was not sent is kept, and the
 * two are apart: changing the doctor of a case leaves its patient as it was.
 * The request of 6 October 2026 (second), items 4 and 5, and (third), item 3.
 */
export const UpdateJobOrderSchema = CreateJobOrderSchema.omit({
  startDate: true,
  deliveryDate: true,
  spans: true,
  // What a case remakes is settled when it is booked in, and urgency has its
  // own endpoint. Neither is an edit to the case's contents.
  redoOf: true,
  redoOfLegacy: true,
  redoReason: true,
  urgent: true,
  // Where a case STARTED is a fact about the past. Moving one now is Mark done,
  // Flag or a take-over, each of which writes a history entry saying so.
  startStage: true,
})
  .partial()
  .extend({ spans: z.array(NewSpanSchema).optional() });

/**
 * Response 201.
 *
 * ONE kit per job order, carrying every tooth on the case and a single QR
 * code. It used to be one kit per tooth for a unit product; the client settled
 * that open question the other way, because four crowns for one patient are
 * four units of work on one tray, not four trays to lose separately.
 *
 * The array shape stays. A job order holding several kits is still how a case
 * split across products would be represented, and narrowing the response to a
 * single object would close that door for no gain.
 */
export const CreateJobOrderResultSchema = z.object({
  jobOrder: JobOrderSchema,
  kits: z.array(KitSchema),
});

/**
 * `PATCH /job-orders/{jo}`: the case as saved, and an undo when the edit
 * changed the input type or an item, which puts the whole edit back for a
 * moment. The lab's request of 18 October 2026.
 */
export const UpdateJobOrderResultSchema = CreateJobOrderResultSchema.extend({
  undo: UndoRefSchema.nullable().optional(),
});

/**
 * `GET /job-orders/counts`: the admin's figures on the Production line and the
 * Orders list, each the size of the list it opens. The lab's request of
 * 17 October 2026, item 5. `inHand` is every case in the lab, neither
 * dispatched nor archived; `active` those still being made, not every kit
 * Ready (item 3: a case in Ready or Logistics is not active); `dueToday` and
 * `overdue` by the rule `due` filters by; `urgent` the urgent cases in hand;
 * `held` the cases on hold.
 */
export const JobOrderCountsSchema = z.object({
  inHand: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  dueToday: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  urgent: z.number().int().nonnegative(),
  held: z.number().int().nonnegative(),
});

export const SetOrderDatesSchema = z.object({
  startDate: CalendarDateSchema,
  deliveryDate: CalendarDateSchema,
});

export const MarkPaidSchema = z.object({
  paid: z.literal(true),
  reference: z.string().optional(),
  paidAt: InstantSchema.optional(),
});

export const HoldOrderSchema = z.object({
  reason: z.string().min(1, 'Give a reason for the hold'),
});

/**
 * `POST /job-orders/{jo}/deletion`. Asks for a case to be deleted, or deletes it
 * outright for a caller holding approveDeletion. The reason is required and
 * short: it is read on a banner and in a list, not in a report.
 */
export const DeleteCaseSchema = z.object({
  reason: z
    .string({ required_error: DELETION_REFUSAL.reasonRequired })
    .trim()
    .min(1, DELETION_REFUSAL.reasonRequired)
    .max(DELETION_REASON_MAX, DELETION_REFUSAL.reasonTooLong),
});

/**
 * `POST /job-orders/{jo}/dispatch`. The case leaves the lab, by courier or in
 * the lab's own van.
 *
 * The tracking code is always typed by hand from the waybill and is never
 * generated, section 4.3 and item 5 of section 17. It is optional at hand-over,
 * because an international case usually has no code until the next day, and it
 * is refused outright on a lab delivery. A courier case needs its courier.
 */
export const DispatchOrderSchema = z.object({
  method: ShippingMethodSchema,
  carrier: CarrierSchema.optional(),
  /**
   * The courier's name, when `carrier` is `Other`. Section 8.2 of the request
   * of 16 September 2026: a case going out with anybody but the four could not
   * be recorded at all.
   *
   * The server RESOLVES it: what comes back on the order is the name, not
   * `Other` and a name beside it, so every screen and every report reads one
   * field and none of them has to remember the special case. Ignored when
   * `carrier` is one of the four, because a name for DHL is not a fact.
   */
  carrierOther: z
    .string()
    .trim()
    .min(CARRIER_OTHER_MIN, SHIPPING_REFUSAL.carrierNameTooShort)
    .max(CARRIER_OTHER_MAX, SHIPPING_REFUSAL.carrierNameTooLong)
    .optional(),
  /** Optional at hand-over: an international case usually has no code until the next day. */
  trackingCode: z.string().min(WAYBILL_CODE_MIN, SHIPPING_REFUSAL.codeTooShort).optional(),
  driverName: z.string().max(DRIVER_NAME_MAX).optional(),
  note: z.string().optional(),
  /** Defaults to now. Backdating is allowed: the van left before anybody reached a screen. */
  dispatchedAt: InstantSchema.optional(),
});

/**
 * `PATCH /job-orders/{jo}/shipping`, with If-Match. The code typed in the
 * morning after, or a correction to any of it later. Send only what changes; a
 * null clears a field. Setting the code on a courier case awaiting it moves the
 * case to in transit and stamps `trackedAt`, and a null `deliveredAt` takes back
 * a received mark somebody put on the wrong row.
 *
 * `trackedAt` is here as well as the six fields section 1.5 lists, because
 * section 1.4 makes all three dates correctable, handed over, in transit and
 * delivered, and the in-transit date is this one.
 */
export const UpdateShippingSchema = z.object({
  /**
   * How it left, corrected after it left: the lab's request of 22 October 2026.
   * A case that now went by courier names its courier in the same edit; one the
   * lab drove keeps no courier, waybill code or code date.
   */
  method: ShippingMethodSchema.optional(),
  carrier: CarrierSchema.optional(),
  /**
   * The courier's name, when `carrier` is `Other`. Section 8.2 of the request
   * of 16 September 2026: a case going out with anybody but the four could not
   * be recorded at all.
   *
   * The server RESOLVES it: what comes back on the order is the name, not
   * `Other` and a name beside it, so every screen and every report reads one
   * field and none of them has to remember the special case. Ignored when
   * `carrier` is one of the four, because a name for DHL is not a fact.
   */
  carrierOther: z
    .string()
    .trim()
    .min(CARRIER_OTHER_MIN, SHIPPING_REFUSAL.carrierNameTooShort)
    .max(CARRIER_OTHER_MAX, SHIPPING_REFUSAL.carrierNameTooLong)
    .optional(),
  trackingCode: z.string().min(WAYBILL_CODE_MIN, SHIPPING_REFUSAL.codeTooShort).nullable().optional(),
  driverName: z.string().max(DRIVER_NAME_MAX).nullable().optional(),
  note: z.string().nullable().optional(),
  dispatchedAt: InstantSchema.optional(),
  trackedAt: InstantSchema.nullable().optional(),
  deliveredAt: InstantSchema.nullable().optional(),
  receivedBy: z.string().nullable().optional(),
});

/**
 * `POST /job-orders/{jo}/shipping/delivered`, with If-Match. `deliveredAt`
 * defaults to now. Refused, 409, on a case already marked received: the day and
 * the signer are corrected, and the mark taken back, through
 * `UpdateShippingSchema`.
 */
export const MarkDeliveredSchema = z.object({
  deliveredAt: InstantSchema.optional(),
  receivedBy: z.string().optional(),
});

/**
 * `GET /archive/{jo}`, the case record. A full screen rather than a drawer:
 * case details, the odontogram with the treated teeth marked, case photos, the
 * full stage history with employee and timings, and delivery and tracking
 * details. Section 5.2.
 */
export const CaseRecordSchema = z.object({
  jobOrder: JobOrderSchema,
  kits: z.array(KitSchema),
  teeth: TeethSchema,
  history: z.array(StageHistoryEntrySchema),
  photos: z.array(CasePhotoSchema),
});

export type JobOrder = z.infer<typeof JobOrderSchema>;
export type JobOrderCounts = z.infer<typeof JobOrderCountsSchema>;
export type CreateJobOrderCommand = z.infer<typeof CreateJobOrderSchema>;
export type DispatchOrderCommand = z.infer<typeof DispatchOrderSchema>;
export type UpdateShippingCommand = z.infer<typeof UpdateShippingSchema>;
export type MarkDeliveredCommand = z.infer<typeof MarkDeliveredSchema>;
